import { useEffect } from 'react';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { MutationDefinition } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import errorResponse from "../errorResponse";
import { InforErrorObj } from "../errorUtils";

/**
 * Wraps an RTK Query mutation hook to automatically handle success/error side-effects via callbacks while exposing the trigger function and all mutation state.
 *
 * Calls the provided errorAction callback on error, or falls back to errorResponse(errorResponse) if no callback is given, and calls successAction on successful completion.
 *
 * @param mutationHook - An RTK Query mutation hook (e.g. from `endpoint.useMutation`) used to trigger and track the mutation.
 * @param successAction - Optional callback invoked with the mutation result when `isSuccess` is `true`.
 * @param errorAction - Optional callback invoked with the error when `isError` is `true` — falls back to `errorResponse` if omitted.
 */
export default function useAgMutation<T>(
    mutationHook: UseMutation<MutationDefinition<any, any, any, T, any>>,
    successAction?: (val?: any) => void,
    errorAction?: (err?: any) => void
) {
    const [trigger, { isLoading, isSuccess, isError, error, data }] = mutationHook();

    useEffect(() => {
        if (isError) {
            if (errorAction) {
                errorAction(error);
            } else {
                errorResponse(error as InforErrorObj);
            }
        }
    }, [isError, error, data]);

    useEffect(() => {
        if (isSuccess) {
            if (successAction) successAction(data);
        }
    }, [isSuccess, data]);

    return { trigger, isLoading, isSuccess, isError, error, data };
}
